import { Link, useSearchParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { EmptyState, PageHeader } from "@/components/Layout";
import { PHASE_LABEL, SessionStateLabel } from "@/components/session";
import { SessionTrade } from "@/components/trade";
import { ErrorNote, useCanWrite } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { useSession, useSessions } from "@/lib/queries";
import type { SessionListItem } from "@/lib/types";

/*
 * La voce da cui si opera ogni giorno.
 *
 * Tre liste, e sono tre perche' sono le tre domande di una giornata: che cosa
 * ho a mercato adesso, che cosa ho gia' fatto oggi, che cosa mi resta da
 * aprire. Un'operazione e' una COPPIA, quindi "a mercato" vuol dire una coppia
 * aperta, non una gamba.
 *
 * Il giorno e' quello del contatore `opsToday`: dalla mezzanotte di
 * Europe/Rome. A mezzanotte le Fasi senza operazioni tornano in "Da aprire" da
 * sole — non c'e' nessun job che le sposta, perche' non e' uno stato scritto da
 * qualche parte: e' una LETTURA di un numero che il backend ricalcola a ogni
 * richiesta.
 */

/** Le Fasi non concluse: quelle vive piu' l'attesa di payout, che non opera ma esiste. */
const OPERABILI = ["ACTIVE", "PAUSED", "AWAITING_PAYOUT"];

export function Trade() {
  const canWrite = useCanWrite();
  const [params, setParams] = useSearchParams();
  const sessions = useSessions({ state: OPERABILI });
  const list = sessions.data ?? [];

  // La scelta sta nell'indirizzo: `/operazione?fase=<id>`.
  const requested = params.get("fase");
  const selected = list.find((s) => s.id === requested) ?? null;

  const header = <PageHeader title="Operazione" subtitle="Direzione, piano e apertura della coppia sulla fase scelta" />;

  if (sessions.isLoading) {
    return (
      <>
        {header}
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      </>
    );
  }
  if (sessions.error) {
    return (
      <>
        {header}
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(sessions.error)}</ErrorNote>
        </div>
      </>
    );
  }
  if (!list.length) {
    return (
      <>
        {header}
        <EmptyState
          title="Nessuna fase attiva"
          hint="Le operazioni si aprono dentro una fase: creane una scegliendo cliente, conti e parametri, poi torna qui per aprire la coppia."
          action={
            canWrite && (
              <Link to="/fasi/nuova" className="inline-flex rounded bg-accent px-3 py-1.5 text-sm font-medium text-page hover:bg-accent/90">
                Nuova fase
              </Link>
            )
          }
        />
      </>
    );
  }

  if (selected) {
    return (
      <>
        {header}
        <div className="space-y-5 px-6 py-5">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <button onClick={() => setParams({}, { replace: true })} className="text-muted hover:text-ink hover:underline">
              ← Tutte le fasi
            </button>
            <span className="text-ink">{selected.cycle.client.name}</span>
            <span className="text-muted">{cicloDi(selected)}</span>
            <Link to={`/fasi/${selected.id}`} className="ml-auto text-muted hover:text-ink hover:underline">
              Dettaglio della fase
            </Link>
          </div>
          <SelectedSession id={selected.id} canWrite={canWrite} />
        </div>
      </>
    );
  }

  /*
   * Una coppia a mercato ha la precedenza su tutto: una Fase con una coppia
   * aperta sta li' e basta, anche se oggi ha gia' aperto e chiuso altro.
   */
  const aMercato = list.filter((s) => s.summary.openPairs > 0);
  const chiuseOggi = list.filter((s) => s.summary.openPairs === 0 && s.summary.opsToday > 0);
  /*
   * "Da aprire" e' l'elenco su cui si CLICCA, quindi contiene solo Fasi su cui
   * si puo' davvero aprire: niente pausa — va ripresa — e niente attesa di
   * payout, dove il tratto e' chiuso e un P&L nuovo non avrebbe dove andare.
   */
  const daAprire = list.filter((s) => s.state === "ACTIVE" && s.summary.openPairs === 0 && s.summary.opsToday === 0);
  const ferme = list.filter((s) => s.state !== "ACTIVE" && s.summary.openPairs === 0 && s.summary.opsToday === 0);

  const apri = (id: string) => setParams({ fase: id }, { replace: true });

  return (
    <>
      {header}
      <div className="space-y-6 px-6 py-5">
        {/* Una Fase scelta che nel frattempo si è chiusa non sparisce in
            silenzio: si dice, o si cerca una riga che non c'è più. */}
        {requested && (
          <p className="text-sm text-muted">
            La fase scelta non è più fra quelle su cui si opera: il suo storico resta nel{" "}
            <Link to={`/fasi/${requested}`} className="underline hover:text-ink">
              dettaglio della fase
            </Link>
            .
          </p>
        )}
        <Lista
          title="Aperte adesso"
          hint="Una coppia a mercato: si chiude da qui."
          empty="Niente a mercato in questo momento."
          rows={aMercato}
          onOpen={apri}
        />
        <Lista
          title="Aperte e chiuse oggi"
          hint="Hanno operato dalla mezzanotte di oggi (Europe/Rome) e adesso sono piatte."
          empty="Nessuna fase ha ancora operato oggi."
          rows={chiuseOggi}
          onOpen={apri}
        />
        <Lista
          title="Da aprire"
          hint="Attive, senza operazioni di oggi: clicca per aprire la coppia."
          empty="Tutte le fasi attive hanno già operato oggi."
          rows={daAprire}
          onOpen={apri}
          footer={
            ferme.length > 0 && (
              <p className="px-4 py-2 text-xs text-faint">
                {ferme.length === 1 ? "Una fase non compare" : `${ferme.length} fasi non compaiono`} qui:{" "}
                {ferme.map((s) => `${s.cycle.client.name} (${LABELS.sessionState[s.state].toLowerCase()})`).join(", ")}. Si
                riprendono dal{" "}
                <Link to="/fasi" className="underline hover:text-ink">
                  dettaglio della fase
                </Link>
                .
              </p>
            )
          }
        />
      </div>
    </>
  );
}

/** Il ciclo scritto per una riga: l'etichetta se c'è, altrimenti firm e preset. */
const cicloDi = (s: SessionListItem) => s.cycle.label ?? `${s.cycle.preset.propFirm.name} ${s.cycle.preset.name}`;

function Lista({
  title,
  hint,
  empty,
  rows,
  onOpen,
  footer,
}: {
  title: string;
  hint: string;
  empty: string;
  rows: SessionListItem[];
  onOpen: (id: string) => void;
  footer?: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-sm font-medium text-ink">
          {title} <span className="text-faint">{rows.length}</span>
        </h2>
        <p className="text-xs text-faint">{hint}</p>
      </div>
      <div className="card divide-y divide-line">
        {rows.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted">{empty}</p>
        ) : (
          rows.map((s) => <Riga key={s.id} s={s} onOpen={onOpen} />)
        )}
        {footer}
      </div>
    </section>
  );
}

function Riga({ s, onOpen }: { s: SessionListItem; onOpen: (id: string) => void }) {
  // Il simbolo predefinito della Fase, com'è scritto sui due server: dal
  // browser si mostra, non si sceglie — la scelta è dell'operazione.
  const simbolo = s.symbols.prop ?? s.canonicalSymbol;
  return (
    <button
      type="button"
      onClick={() => onOpen(s.id)}
      className="flex w-full flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 text-left text-sm hover:bg-raised/40"
    >
      <span className="font-medium text-ink">{s.cycle.client.name}</span>
      <span className="text-muted">{cicloDi(s)}</span>
      <span className="text-ink">{PHASE_LABEL(s.phase)}</span>
      {s.state !== "ACTIVE" && <SessionStateLabel state={s.state} />}
      <span className="text-prop">{s.propAccount.label}</span>
      <span className="text-broker">{s.brokerAccount.label}</span>
      <span className="text-muted" title={`Prop ${s.symbols.prop ?? "non mappato"} contro broker ${s.symbols.broker ?? "non mappato"}`}>
        {simbolo}
      </span>
      <span className="ml-auto text-muted" title="Coppie con almeno un ticket dalla mezzanotte di oggi (Europe/Rome)">
        Oggi: {s.summary.opsToday}
      </span>
    </button>
  );
}

function SelectedSession({ id, canWrite }: { id: string; canWrite: boolean }) {
  const session = useSession(id);
  if (session.isLoading) return <p className="text-sm text-muted">Caricamento della fase…</p>;
  if (session.error || !session.data) return <ErrorNote>{session.error ? errorMessage(session.error) : "Fase non trovata"}</ErrorNote>;

  const s = session.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
        <SessionStateLabel state={s.state} />
        <span>
          Predefinito: {s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro {s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol} · oggi: {s.summary.opsToday}{" "}
          {s.summary.opsToday === 1 ? "operazione" : "operazioni"}
        </span>
      </div>
      <SessionTrade s={s} canWrite={canWrite} />
    </div>
  );
}
